import React from 'react';
import PropTypes from 'prop-types';
import { useReactTable, getCoreRowModel, flexRender } from '@tanstack/react-table';
import styles from './Table.module.scss';

/* ────────────────────────────────────────────────────────────
 * Table — low-level primitive for nested or non-page-level
 * tables (e.g. a breakdown inside an expandable row, a small
 * table inside a modal).
 *
 * Two render modes:
 *
 * 1. TanStack mode (preferred for new code). Pass `data` and
 *    `columns`; the component runs `useReactTable` internally
 *    and renders the head/body via `flexRender`.
 *
 *      <Table data={rows} columns={columns} hoverable />
 *
 * 2. Legacy children mode (existing screens). Pass raw <thead>
 *    / <tbody> as children — kept so the migration to TanStack
 *    can happen screen-by-screen without breaking the world.
 *
 *      <Table>
 *        <thead>...</thead>
 *        <tbody>...</tbody>
 *      </Table>
 *
 * Use <DataTable> instead for top-level page tables — it adds
 * the scroll wrapper, sticky header, and brand-tinted chrome.
 * ──────────────────────────────────────────────────────────── */

const EMPTY_ARRAY = [];

const Table = ({
  children,
  striped,
  hoverable,
  compact,
  className,
  data,
  columns,
  tableOptions,
  emptyMessage,
  onRowClick,
  getRowProps,
  ...rest
}) => {
  const usingTanstack = Array.isArray(data) && Array.isArray(columns);

  const table = useReactTable({
    data: usingTanstack ? data : EMPTY_ARRAY,
    columns: usingTanstack ? columns : EMPTY_ARRAY,
    getCoreRowModel: getCoreRowModel(),
    ...(tableOptions || {}),
  });

  const classNames = [
    styles.table,
    striped ? styles.striped : '',
    hoverable ? styles.hoverable : '',
    compact ? styles.compact : '',
    className || '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <table className={classNames} {...rest}>
      {usingTanstack ? (
        <>
          <thead>
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((header) => (
                  <th key={header.id} colSpan={header.colSpan}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className={styles.empty}>
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => {
                const extra = getRowProps ? getRowProps(row) : null;
                return (
                  <tr
                    key={row.id}
                    onClick={onRowClick ? () => onRowClick(row.original, row) : undefined}
                    {...(extra || {})}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </>
      ) : (
        children
      )}
    </table>
  );
};

Table.propTypes = {
  children: PropTypes.node,
  striped: PropTypes.bool,
  hoverable: PropTypes.bool,
  compact: PropTypes.bool,
  className: PropTypes.string,
  data: PropTypes.array,
  columns: PropTypes.array,
  tableOptions: PropTypes.object,
  emptyMessage: PropTypes.node,
  onRowClick: PropTypes.func,
  getRowProps: PropTypes.func,
};

Table.defaultProps = {
  children: null,
  striped: false,
  hoverable: false,
  compact: false,
  className: '',
  data: undefined,
  columns: undefined,
  tableOptions: undefined,
  emptyMessage: 'No records found',
  onRowClick: undefined,
  getRowProps: undefined,
};

export default Table;
